import { useCallback, useMemo, useState } from 'react'
import { Pencil, Plus, ShoppingBag, EyeOff } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchPointsCatalog,
  upsertPointsCatalog,
  deletePointsCatalog,
  type AdminPointsCatalogRow,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'

type EditTarget = AdminPointsCatalogRow | 'new' | null

/**
 * A21.04 — Ball spend-katalogi (28 §3). Kosmetik/boost/gift elementlari; narx/turkum/
 * enabled admin'dan (`points_catalog`). O'chirish = soft-disable (sotib-olish tarixi qoladi).
 */
export function PointsCatalog() {
  const t = useT()
  const { toast } = useToast()
  const [target, setTarget] = useState<EditTarget>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.points.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.points.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
        throw e
      }
    },
    [toast, t],
  )

  const catLabel = (c: string) => (c === 'boost' ? t.points.catBoost : c === 'gift' ? t.points.catGift : t.points.catCosmetic)

  const columns = useMemo<AdminColumn<AdminPointsCatalogRow>[]>(
    () => [
      {
        key: 'title',
        header: t.points.itemTitle,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title}</span>
            <span className="block truncate font-mono text-xs text-tertiary">{r.code}</span>
          </div>
        ),
      },
      {
        key: 'price',
        header: t.points.price,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => <span className="block text-right font-medium text-accent">{r.price}</span>,
      },
      { key: 'category', header: t.points.category, type: 'text', width: 120, render: (r) => catLabel(r.category) },
      { key: 'purchases', header: t.points.purchases, type: 'number', width: 120, align: 'right' },
      {
        key: 'enabled',
        header: t.points.enabled,
        type: 'text',
        width: 120,
        render: (r) => <StatusBadge status={r.enabled ? 'active' : 'deleted'} label={r.enabled ? t.points.enabled : t.points.disable} />,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminPointsCatalogRow>[]>(
    () => [
      { id: 'edit', label: t.points.editItem, icon: <Pencil size={13} />, onSelect: (r) => setTarget(r) },
      {
        id: 'disable',
        label: t.points.disable,
        icon: <EyeOff size={13} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: (r) => void run(() => deletePointsCatalog(r.code)),
      },
    ],
    [t, run],
  )

  return (
    <>
      <PageHeader
        title={t.points.catalog}
        screenId="BALL.02"
        actions={
          <Button size="sm" onClick={() => setTarget('new')}>
            <Plus size={14} /> {t.points.addItem}
          </Button>
        }
      />
      <DataTable<AdminPointsCatalogRow>
        key={reloadKey}
        screenId="BALL.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchPointsCatalog}
        rowId={(r) => r.code}
        rowActions={rowActions}
        emptyState={{ icon: <ShoppingBag size={22} />, title: t.points.catalog }}
      />
      {target && (
        <CatalogDialog
          target={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function CatalogDialog({ target, onClose, onSaved }: { target: EditTarget; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const { toast } = useToast()
  const editing = target !== 'new' && target !== null ? target : null
  const [code, setCode] = useState(editing?.code ?? '')
  const [title, setTitle] = useState(editing?.title ?? '')
  const [price, setPrice] = useState(String(editing?.price ?? 0))
  const [category, setCategory] = useState(editing?.category ?? 'cosmetic')
  const [enabled, setEnabled] = useState(editing?.enabled ?? true)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!code.trim() || !title.trim()) return
    setBusy(true)
    try {
      await upsertPointsCatalog({
        code: code.trim(),
        title: title.trim(),
        price: parseInt(price, 10) || 0,
        category,
        enabled,
      })
      toast(t.points.done, { tone: 'success' })
      onSaved()
    } catch (e) {
      setBusy(false)
      toast(t.points.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? t.points.editItem : t.points.newItem}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.points.cancel}
          </Button>
          <Button onClick={save} disabled={busy || !code.trim() || !title.trim()}>
            {t.points.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.points.code}</span>
          <Input value={code} onChange={(e) => setCode(e.target.value)} mono disabled={!!editing} placeholder="profile_color" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.points.itemTitle}</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.points.price}</span>
          <Input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="numeric" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">{t.points.category}</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-8 rounded-md border border-border-default bg-surface-1 px-2 text-base text-primary"
          >
            <option value="cosmetic">{t.points.catCosmetic}</option>
            <option value="boost">{t.points.catBoost}</option>
            <option value="gift">{t.points.catGift}</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="text-secondary">{t.points.enabled}</span>
        </label>
      </div>
    </Dialog>
  )
}
